import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  createOpportunitySchedule,
  getOpportunitySchedulesByClient,
  updateOpportunitySchedule,
  deleteOpportunitySchedule,
  getOpportunitiesByClient,
  setStoreData,
  getJoinedGreenUsers,
} from '../../Redux/actions';
import {
  Box, Button, Chip, CircularProgress, Grid, IconButton, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, Dialog,
  DialogActions, DialogContent, DialogTitle, TextField, Autocomplete,
  Typography, Pagination, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ConfirmDialog from '../Common/ConfirmDialog';
import { datePickerSlotProps, datePickerTextFieldStyle, paginationStyle, THEME_COLOR, inputLabelFocusedStyle, selectFocusedStyle, buttonStyles } from '../../theme/formStyles';

const MEETING_TYPES = ['In Person', 'Online'];

const emptyForm = () => ({
  opportunity: null,
  schedule_date: null,
  location: null,
  bd_owner: null,
  meeting_type: 'In Person',
  description: '',
});

export default function OpportunitySchedule() {
  const { client_id } = useParams();
  const dispatch = useDispatch();

  const [schedules, setSchedules] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [greenUsers, setGreenUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchSchedules(1);
    fetchOpportunities();
    fetchGreenUsers();
  }, [client_id]);

  const fetchSchedules = (page = 1) => {
    setLoading(true);
    dispatch(getOpportunitySchedulesByClient(client_id, { page }))
      .then((res) => {
        if (res?.data?.success) {
          setSchedules(res.data.data || []);
          setTotalPages(res.data.totalPages || 0);
          setCurrentPage(res.data.currentPage || 1);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const fetchOpportunities = () => {
    dispatch(getOpportunitiesByClient(client_id)).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        setOpportunities(res.data.data || []);
      }
    });
  };

  const fetchGreenUsers = () => {
    dispatch(getJoinedGreenUsers()).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        let users = res.data.allData || res.data.data || [];
        users = users.filter((u) => (u.user_id || '').toLowerCase().includes('@greenvironment'));
        setGreenUsers(users);
      }
    });
  };

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const handleOpenEdit = (item) => {
    const opp = opportunities.find((o) => o.id === item.opportunity_id) || null;
    setEditingId(item.id);
    setForm({
      opportunity: opp,
      schedule_date: item.schedule_date ? dayjs(item.schedule_date) : dayjs(),
      location: item.location || null,
      bd_owner: item.bd_owner || null,
      meeting_type: item.meeting_type || 'In Person',
      description: item.description || '',
    });
    setDialogOpen(true);
  };

  const handleOpportunityChange = (opp) => {
    setForm((prev) => ({
      ...prev,
      opportunity: opp,
      location: opp?.location || null,
      bd_owner: null,
    }));
  };

  const handleSave = () => {
    if (!form.opportunity || !form.schedule_date || !form.bd_owner) return;
    setSaving(true);
    const payload = {
      opportunity_id: form.opportunity.id,
      client_id,
      schedule_date: form.schedule_date.format('YYYY-MM-DD'),
      location: form.location?.id || null,
      bd_owner: form.bd_owner,
      meeting_type: form.meeting_type,
      description: form.description,
    };
    const action = editingId
      ? dispatch(updateOpportunitySchedule(editingId, payload))
      : dispatch(createOpportunitySchedule(payload));

    action
      .then((res) => {
        setSaving(false);
        if (res?.data?.success) {
          setDialogOpen(false);
          fetchSchedules(currentPage);
          dispatch(
            setStoreData('alertMessage', {
              msg: editingId ? 'Schedule updated successfully' : 'Schedule created successfully',
              type: 'success',
              handleClose: () => dispatch(setStoreData('alertMessage', null)),
            })
          );
        } else {
          dispatch(
            setStoreData('alertMessage', {
              msg: res?.data?.message || 'Operation failed',
              type: 'fail',
              handleClose: () => dispatch(setStoreData('alertMessage', null)),
            })
          );
        }
      })
      .catch(() => setSaving(false));
  };

  const handleDeleteConfirm = () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: null });
    dispatch(deleteOpportunitySchedule(id)).then((res) => {
      if (res?.data?.success) {
        fetchSchedules(currentPage);
        dispatch(
          setStoreData('alertMessage', {
            msg: 'Schedule deleted successfully',
            type: 'success',
            handleClose: () => dispatch(setStoreData('alertMessage', null)),
          })
        );
      }
    });
  };

  const handlePageChange = (e, page) => {
    setCurrentPage(page);
    fetchSchedules(page);
  };

  const tableColumns = [
    'Sl.No.', 'Opportunity Name', 'Date', 'Location', 'BD Owner / SPOC', 'Meeting Type', 'Description', '',
  ];

  const getMeetingTypeChipStyle = (type) => {
    if (type === 'Online') {
      return { color: '#2e7d6e', bgcolor: '#e8f5f2', border: '1px solid #b2d8d0' };
    }
    return { color: '#6a3db5', bgcolor: '#f0ebff', border: '1px solid #c9b8f0' };
  };



  let tableContent;
  if (loading) {
    tableContent = (
      <TableRow>
        <TableCell colSpan={8} align="center">
          <CircularProgress sx={{ color: THEME_COLOR }} />
        </TableCell>
      </TableRow>
    );
  } else if (schedules.length === 0) {
    tableContent = (
      <TableRow>
        <TableCell colSpan={8} align="center">
          <h5 className="w3-text-grey">No Schedules Found</h5>
        </TableCell>
      </TableRow>
    );
  } else {
    tableContent = schedules.map((item, index) => (
      <TableRow
        key={item.id}
        onMouseEnter={() => setHoveredRowId(item.id)}
        onMouseLeave={() => setHoveredRowId(null)}
      >
        <TableCell>{(currentPage - 1) * 20 + index + 1}</TableCell>
        <TableCell>{item.opportunity?.opportunity_name || '-'}</TableCell>
        <TableCell>
          {item.schedule_date
            ? new Date(item.schedule_date).toLocaleDateString('en-GB')
            : '-'}
        </TableCell>
        <TableCell>{item.location?.name || '-'}</TableCell>
        <TableCell>{item.bd_owner?.name || '-'}</TableCell>
        <TableCell>
          <Chip
            label={item.meeting_type || 'In Person'}
            size="small"
            sx={{ minWidth: '72px', fontWeight: 600, ...getMeetingTypeChipStyle(item.meeting_type) }}
          />
        </TableCell>
        <TableCell sx={{ maxWidth: 240, color: 'text.secondary', fontSize: '13px' }}>
          {item.description || '-'}
        </TableCell>
        <TableCell>
          <Box display="flex" justifyContent="right">
            <Tooltip title="Edit">
              <IconButton
                style={{ opacity: hoveredRowId === item.id ? 1 : 0, paddingLeft: '0px' }}
                onClick={() => handleOpenEdit(item)}
              >
                <Edit style={{ color: 'black' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                style={{ opacity: hoveredRowId === item.id ? 1 : 0, paddingLeft: '0px' }}
                onClick={() => setConfirmDialog({ open: true, id: item.id })}
              >
                <Delete style={{ color: '#d32f2f' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
    ));
  }

  return (
    <div>
      <Box sx={{ paddingY: 2 }}>
        <Button
          variant="contained"
          style={{ backgroundColor: THEME_COLOR, color: 'white' }}
          onClick={handleOpenCreate}
        >
          Create Schedule
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table aria-label="opportunity schedules table" style={{ tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              {tableColumns.map((col, idx) => (
                <TableCell key={idx} style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{tableContent}</TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            sx={paginationStyle}
          />
        </Box>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: `2px solid ${THEME_COLOR}`, fontWeight: 600, fontSize: '18px', color: THEME_COLOR }}>
          BD Client Meet Schedule
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={opportunities}
                getOptionLabel={(o) => o.opportunity_name || ''}
                value={form.opportunity}
                onChange={(e, val) => handleOpportunityChange(val)}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Opportunity Name *"
                    placeholder="Select opportunity"
                    size="small"
                    fullWidth
                    sx={datePickerTextFieldStyle}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Location"
                value={form.location?.name || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date *"
                  value={form.schedule_date}
                  onChange={(val) => setForm((prev) => ({ ...prev, schedule_date: val }))}
                  format="DD/MM/YYYY"
                  slotProps={datePickerSlotProps({ size: 'small', fullWidth: true, label: 'Date *' })}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={greenUsers}
                getOptionLabel={(o) => o.name ? (o.user_id ? `${o.name} (${o.user_id})` : o.name) : ''}
                value={form.bd_owner}
                onChange={(e, val) => setForm((prev) => ({ ...prev, bd_owner: val }))}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="BD Owner / SPOC *"
                    placeholder="Select SPOC"
                    size="small"
                    fullWidth
                    sx={datePickerTextFieldStyle}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={inputLabelFocusedStyle}>Meeting Type</InputLabel>
                <Select
                  label="Meeting Type"
                  value={form.meeting_type}
                  onChange={(e) => setForm((prev) => ({ ...prev, meeting_type: e.target.value }))}
                  sx={selectFocusedStyle}
                  MenuProps={{ disableScrollLock: true }}
                >
                  {MEETING_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                placeholder="Agenda, notes, or context for this client meeting..."
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                sx={datePickerTextFieldStyle}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: '#f0f9f9',
              border: '1px solid #b2d8d8',
              borderRadius: 1,
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start',
            }}
          >
            <CalendarTodayIcon sx={{ color: '#c62828', fontSize: 20, mt: 0.2, flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary">
              Once saved, this meet will automatically appear on the Master Schedule under the selected
              SPOC on the chosen date — alongside Calibration, Project Tasks, and Ticket Site Visits.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setDialogOpen(false)} disabled={saving} sx={buttonStyles.outlined}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.opportunity || !form.schedule_date || !form.bd_owner}
            sx={buttonStyles.contained}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : editingId ? (
              'Update'
            ) : (
              'Save & Add to Calendar'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {confirmDialog.open && (
        <ConfirmDialog
          handleCancel={() => setConfirmDialog({ open: false, id: null })}
          handleOk={handleDeleteConfirm}
          message="Are you sure you want to delete this schedule?"
        />
      )}
    </div>
  );
}
